/**
 * @typedef {Object} UserPlan
 * @property {number|string} id
 * @property {string} company
 * @property {string} email
 * @property {string} plan
 * @property {string} status
 * @property {string} joinDate
 * @property {string} expiryDate
 * @property {string} lastPayment
 * @property {string} source
 * @property {string} notes
 */

/**
 * @typedef {Object} CreateUserPlanRequest
 * @property {string} [company]
 * @property {string} [email]
 * @property {string} [plan]
 * @property {string} [status]
 * @property {string} [joinDate]
 * @property {string} [expiryDate]
 * @property {string} [lastPayment]
 * @property {string} [source]
 * @property {string} [notes]
 */

/**
 * @typedef {CreateUserPlanRequest & { id: number|string }} UpdateUserPlanRequest
 */

/**
 * @typedef {Object} DeleteUserPlanRequest
 * @property {number|string} id
 */

const EMPTY_USER_PLAN = {
  id: "",
  userId: "",
  company: "",
  email: "",
  plan: "",
  status: "",
  statusPlan: true,
  paymentMethod: "",
  joinDate: "",
  expiryDate: "",
  lastPayment: "",
  source: "",
  notes: "",
  createdAt: "",
  updatedAt: "",
};

const asString = (value) => (value === null || value === undefined ? "" : String(value));

const getCompanyName = (companyValue) => {
  if (!companyValue) return "";
  if (typeof companyValue === "string") return companyValue;
  if (typeof companyValue === "object") {
    return asString(companyValue.name ?? companyValue.companyName ?? "");
  }
  return asString(companyValue);
};

/**
 * @param {Object} user
 * @returns {number|null}
 */
export const getUserId = (user) => {
  if (!user || typeof user !== "object") return null;
  const candidates = [user.userId, user.user_id];
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isInteger(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const resolveStatusPlan = (raw = {}, planData = {}) => {
  const value = planData.status_plan ?? raw.status_plan;
  if (typeof value === "boolean") return value;
  if (value === "true" || value === 1) return true;
  if (value === "false" || value === 0) return false;
  return true;
};

const resolveUserActive = (raw = {}) => {
  const value =
    raw.active ??
    raw.user?.active ??
    raw.is_active ??
    raw.user_active ??
    raw.user?.is_active;

  if (typeof value === "boolean") return value;
  if (value === "true" || value === 1 || value === "1") return true;
  if (value === "false" || value === 0 || value === "0") return false;
  return null;
};

/**
 * Normaliza un user-plan de backend a shape del módulo de users.
 * @param {Object} raw
 * @returns {UserPlan}
 */
export const normalizeUserPlan = (raw = {}, options = {}) => {
  const planData =
    raw.plan && typeof raw.plan === "object" && !Array.isArray(raw.plan)
      ? raw.plan
      : {};
  const statusPlan = resolveStatusPlan(raw, planData);
  const userActive = resolveUserActive(raw);

  let normalizedStatus =
    raw.status ??
    raw.userStatus ??
    raw.estado ??
    (statusPlan ? "Activo" : "Inactivo");

  if (userActive === false) {
    normalizedStatus = "Dado de baja";
  }

  const resolvedUserId =
    raw.user_id ?? raw.userId ?? raw.user?.id ?? EMPTY_USER_PLAN.userId;
  const resolvedPlanId = raw.id ?? raw.userPlanId ?? raw.user_plan_id ?? "";

  const normalized = {
    id: resolvedPlanId || EMPTY_USER_PLAN.id,
    userId: resolvedUserId || EMPTY_USER_PLAN.userId,
    company: getCompanyName(
      raw.company ??
        raw.companyName ??
        raw.company_name ??
        raw.empresa ??
        raw.user?.company ??
        raw.user?.companyName ??
        raw.user?.company_name
    ),
    email: asString(raw.email ?? raw.mail ?? raw.user?.email),
    plan: asString(planData.plan ?? raw.planName ?? raw.plan_name ?? raw.plan),
    status: asString(normalizedStatus),
    active: userActive ?? true,
    statusPlan,
    paymentMethod: asString(
      planData.payment_method ?? raw.payment_method ?? raw.paymentMethod
    ),
    joinDate: asString(
      raw.joinDate ?? raw.join_date ?? raw.createdAt ?? raw.created_at
    ),
    expiryDate: asString(
      raw.expiryDate ??
        planData.expiry_date ??
        raw.expiry_date ??
        raw.expirationDate ??
        raw.expiration_date
    ),
    lastPayment: asString(
      raw.lastPayment ??
        planData.last_payment ??
        raw.last_payment ??
        raw.paymentDate ??
        raw.payment_date
    ),
    source: asString(raw.source ?? raw.channel ?? raw.origin),
    notes: asString(raw.notes ?? raw.note ?? raw.observations),
    createdAt: asString(raw.createdAt ?? raw.created_at),
    updatedAt: asString(raw.updatedAt ?? raw.updated_at),
  };

  return {
    ...EMPTY_USER_PLAN,
    ...normalized,
  };
};

/**
 * @param {Partial<UserPlan>} user
 * @returns {CreateUserPlanRequest}
 */
export const toCreateUserPlanRequest = (user = {}) => ({
  company: asString(user.company),
  email: asString(user.email),
  plan: asString(user.plan),
  status: asString(user.status),
  joinDate: asString(user.joinDate),
  expiryDate: asString(user.expiryDate),
  lastPayment: asString(user.lastPayment),
  source: asString(user.source),
  notes: asString(user.notes),
});

/**
 * @param {Partial<UserPlan>} user
 * @returns {UpdateUserPlanRequest}
 */
export const toUpdateUserPlanRequest = (user = {}) => ({
  id: user.id,
  ...toCreateUserPlanRequest(user),
});

/**
 * Arma el payload del API user-plan (GET/PUT) desde el usuario normalizado.
 * @param {Partial<UserPlan>} user
 * @param {Object} overrides
 */
export const toUserPlanApiPayload = (user = {}, overrides = {}) => {
  const userId = getUserId({ ...user, ...overrides }) ?? overrides.user_id;
  const statusPlan =
    overrides.status_plan ??
    overrides.statusPlan ??
    user.statusPlan;

  return {
    id: overrides.id ?? user.id,
    user_id: userId,
    plan: overrides.plan ?? user.plan,
    payment_method:
      overrides.payment_method ??
      overrides.paymentMethod ??
      user.paymentMethod,
    status_plan: statusPlan,
    source: overrides.source ?? user.source,
    notes: overrides.notes ?? user.notes,
  };
};

/**
 * @param {number|string} id
 * @returns {DeleteUserPlanRequest}
 */
export const toDeleteUserPlanRequest = (id) => ({
  id,
});
